import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import { useLanguage } from '../../contexts/LanguageContext';

const BeneficiosPanel = () => {
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const [beneficios, setBeneficios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadBeneficios();
  }, []);

  const loadBeneficios = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/beneficios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBeneficios(response.data);
    } catch (err) {
      console.error('Error loading beneficios:', err);
      setError(t('beneficios.loadError', 'Error al cargar los beneficios'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(dateLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      sent: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800'
    };

    const labels = {
      sent: t('beneficios.status.sent', 'Enviado'),
      pending: t('beneficios.status.pending', 'Pendiente'),
      failed: t('beneficios.status.failed', 'Fallido')
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`} style={{ borderRadius: 0 }}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-gray-500">{t('beneficios.loading', 'Cargando beneficios...')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 text-red-800" style={{ borderRadius: 0 }}>
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg md:text-2xl font-bold text-gray-900">{t('beneficios.title', 'Beneficios')}</h2>
          <p className="text-sm text-gray-600 mt-1">
            {t('beneficios.subtitle', 'Correos registrados para el código de descuento CONTACT2026')}
          </p>
        </div>
        <div className="text-sm text-gray-600">
          {t('beneficios.total', 'Total:')} <span className="font-semibold">{beneficios.length}</span>
        </div>
      </div>

      {/* Table */}
      {beneficios.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 border border-gray-200" style={{ borderRadius: 0 }}>
          <p className="text-gray-700 font-medium">{t('beneficios.empty.title', 'Todavía no hay beneficios registrados')}</p>
          <p className="text-gray-500 text-sm mt-1">{t('beneficios.empty.sub', 'Aparecerán aquí cuando alguien use el código de descuento CONTACT2026')}</p>
        </div>
      ) : (
        <div className="border border-gray-200 overflow-hidden" style={{ borderRadius: 0 }}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 md:px-6 md:py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t('beneficios.table.email', 'Correo Electrónico')}
                  </th>
                  <th className="px-3 py-2 md:px-6 md:py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t('beneficios.table.registeredDate', 'Fecha de Registro')}
                  </th>
                  <th className="px-3 py-2 md:px-6 md:py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t('beneficios.table.status', 'Estado')}
                  </th>
                  <th className="px-3 py-2 md:px-6 md:py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t('beneficios.table.sentDate', 'Fecha de Envío')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {beneficios.map((beneficio) => (
                  <tr key={beneficio.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2 md:px-6 md:py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{beneficio.email}</div>
                    </td>
                    <td className="px-3 py-2 md:px-6 md:py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{formatDate(beneficio.createdAt)}</div>
                    </td>
                    <td className="px-3 py-2 md:px-6 md:py-4 whitespace-nowrap">
                      {getStatusBadge(beneficio.status)}
                    </td>
                    <td className="px-3 py-2 md:px-6 md:py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">
                        {beneficio.emailSentAt ? formatDate(beneficio.emailSentAt) : '-'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-green-50 border border-green-200 p-3 md:p-4" style={{ borderRadius: 0 }}>
          <div className="text-sm text-green-800 font-medium">{t('beneficios.stat.sent', 'Enviados')}</div>
          <div className="text-lg md:text-2xl font-bold text-green-900 mt-1">
            {beneficios.filter(b => b.status === 'sent').length}
          </div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 p-3 md:p-4" style={{ borderRadius: 0 }}>
          <div className="text-sm text-yellow-800 font-medium">{t('beneficios.stat.pending', 'Pendientes')}</div>
          <div className="text-lg md:text-2xl font-bold text-yellow-900 mt-1">
            {beneficios.filter(b => b.status === 'pending').length}
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 p-3 md:p-4" style={{ borderRadius: 0 }}>
          <div className="text-sm text-red-800 font-medium">{t('beneficios.stat.failed', 'Fallidos')}</div>
          <div className="text-lg md:text-2xl font-bold text-red-900 mt-1">
            {beneficios.filter(b => b.status === 'failed').length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeneficiosPanel;
